/**
 * Iter Feb 2026 R19 · Designation ↔ Employee propagation dialog.
 *
 * Two use-modes:
 *  1. `variant="employee"` — used when an employee's designation changes
 *     on the Edit-Employee form. Offers 3 modes for applying the new
 *     designation's duty template to that ONE employee.
 *  2. `variant="template"` — used when a designation's duty template
 *     changes (add/remove duty rows) on the Designations page. Offers
 *     3 modes for propagating the DIFF to every employee currently on
 *     that designation.
 *
 * Custom duties (`source: custom`) are ALWAYS preserved — the backend
 * enforces this so the dialog only surfaces the choice of what to do
 * with `source: designation` duties.
 */
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import api from "@/lib/api";
import { AlertCircle, Users } from "lucide-react";
import { apiErrorMessage } from "@/lib/apiError";

const EMP_MODES = [
  {
    value: "add_and_end_date",
    label: "End-date old duties + add new (recommended)",
    hint: "Prior designation duties get valid_to = valid_from date; new ones start from that date. Full history is preserved.",
  },
  {
    value: "replace_all",
    label: "Replace all designation duties",
    hint: "Old designation duties are removed entirely. Custom duties stay. Use when the change is a fresh start.",
  },
  {
    value: "add_only",
    label: "Add new duties only (keep old active)",
    hint: "Old duties keep running alongside new ones. Rare — usually only for hybrid roles.",
  },
];

const TPL_MODES = [
  { value: "both", label: "Apply full diff (add new + end-date removed)",
    hint: "Recommended. Adds new duties to every employee on this designation and stamps valid_to on removed ones." },
  { value: "add_new", label: "Only add newly-added duties",
    hint: "Ignores removed duty ids — leaves them active on employees." },
  { value: "end_date_removed", label: "Only end-date removed duties",
    hint: "Ignores newly-added duty ids — nothing is inserted." },
];

const today = () => new Date().toISOString().slice(0, 10);

export function EmployeeDesignationPropagationDialog({
  open, onClose, workspaceId, userId, employeeName,
  oldDesignation, newDesignation, onApplied,
}) {
  const [mode, setMode] = useState("add_and_end_date");
  const [validFrom, setValidFrom] = useState(today());
  const [busy, setBusy] = useState(false);

  const apply = async () => {
    if (!newDesignation?.id && !oldDesignation?.id) return;
    setBusy(true);
    try {
      const { data } = await api.post(
        `/workspaces/${workspaceId}/hr/employees/${userId}/change-designation`,
        {
          designation_id: newDesignation?.id || null,
          designation_name: newDesignation?.name || null,
          propagation_mode: mode,
          valid_from: validFrom,
        },
      );
      const parts = [];
      if (data.added) parts.push(`${data.added} added`);
      if (data.ended) parts.push(`${data.ended} end-dated`);
      if (data.removed) parts.push(`${data.removed} removed`);
      toast.success(`Duties updated: ${parts.join(" · ") || "no changes"}`);
      onApplied?.(data);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Propagation failed"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="emp-designation-propagate-dialog">
        <DialogHeader>
          <DialogTitle>Update job duties for {employeeName || "this employee"}?</DialogTitle>
          <DialogDescription className="text-xs pt-1">
            Designation changed from{" "}
            <b>{oldDesignation?.name || "—"}</b> to <b>{newDesignation?.name || "—"}</b>.
            Choose how to apply the change to their job duties. Custom duties are always preserved.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Effective date</Label>
            <Input type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)}
                   data-testid="emp-prop-valid-from" className="mt-1 rounded-none" />
          </div>

          <RadioGroup value={mode} onValueChange={setMode} className="space-y-2">
            {EMP_MODES.map((m) => (
              <div key={m.value}
                   className="flex items-start gap-2 border border-border rounded-none p-2 hover:bg-muted/40 cursor-pointer"
                   onClick={() => setMode(m.value)}>
                <RadioGroupItem value={m.value} id={`emp-mode-${m.value}`} className="mt-1"
                                data-testid={`emp-prop-mode-${m.value}`} />
                <label htmlFor={`emp-mode-${m.value}`} className="flex-1 cursor-pointer">
                  <div className="text-xs font-medium">{m.label}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">{m.hint}</div>
                </label>
              </div>
            ))}
          </RadioGroup>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="emp-prop-cancel" disabled={busy}>
            Skip (just update designation)
          </Button>
          <Button onClick={apply} disabled={busy} data-testid="emp-prop-apply">
            {busy ? "Applying…" : "Apply"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export function TemplatePropagationDialog({
  open, onClose, designationId, designationName,
  addedTemplateIds, removedTemplateIds, editedTemplateIds, onApplied,
}) {
  const [mode, setMode] = useState("both");
  // R32 · When ONLY text edits are pending, default to `update_in_place`
  // (safest — preserves review history against the same duty row).
  const [editMode, setEditMode] = useState("update_in_place");
  const [validFrom, setValidFrom] = useState(today());
  const [preview, setPreview] = useState(null);   // { employee_count, employees, template_duty_count }
  const [editImpact, setEditImpact] = useState(null); // { employees:[], count:0 }
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const hasEdits = (editedTemplateIds?.length || 0) > 0;
  const hasAdds = (addedTemplateIds?.length || 0) > 0;
  const hasRemoves = (removedTemplateIds?.length || 0) > 0;

  const loadPreview = async () => {
    if (!designationId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/hr/designations/${designationId}/impact-preview`);
      setPreview(data);
      // R32 · Fetch which employees will be affected by the text edits
      // in particular. This is the "who will be impacted" list Emergent
      // Support asked us to surface next to the confirm button.
      if (hasEdits) {
        const { data: ei } = await api.post(
          `/hr/designations/${designationId}/edit-impact`,
          { edited_template_ids: editedTemplateIds });
        setEditImpact(ei);
      }
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not load impact preview"));
    } finally { setLoading(false); }
  };

  // Auto-load preview when opened
  if (open && !preview && !loading) loadPreview();

  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/hr/designations/${designationId}/propagate-changes`,
        {
          propagation_mode: mode,
          valid_from: validFrom,
          added_template_ids: addedTemplateIds || [],
          removed_template_ids: removedTemplateIds || [],
          edited_template_ids: editedTemplateIds || [],
          edit_mode: editMode,
        },
      );
      const parts = [];
      if (data.added) parts.push(`${data.added} duties added`);
      if (data.ended) parts.push(`${data.ended} duties end-dated`);
      if (data.edited) parts.push(`${data.edited} duties updated`);
      toast.success(`Propagated to ${data.employees_affected} employees${parts.length ? ` · ${parts.join(" · ")}` : ""}`);
      onApplied?.(data);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Propagation failed"));
    } finally { setBusy(false); }
  };

  const noOp = !hasAdds && !hasRemoves && !hasEdits;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-xl" data-testid="tpl-designation-propagate-dialog">
        <DialogHeader>
          <DialogTitle>Push template changes to employees?</DialogTitle>
          <DialogDescription className="text-xs pt-1">
            <b>{designationName}</b>&apos;s duty template changed —
            {" "}<span className="text-emerald-700 dark:text-emerald-400">{addedTemplateIds?.length || 0} added</span>{" "}·{" "}
            <span className="text-amber-700 dark:text-amber-400">{removedTemplateIds?.length || 0} removed</span>
            {hasEdits ? (
              <>{" "}·{" "}<span className="text-sky-700 dark:text-sky-400">{editedTemplateIds?.length || 0} edited</span></>
            ) : null}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Impact preview */}
          <div className="border border-border rounded-none p-3 bg-muted/30">
            <div className="flex items-center gap-2 text-xs font-medium">
              <Users className="w-3.5 h-3.5" />
              {loading ? "Loading impact preview…" : (
                <span data-testid="tpl-prop-impact-count">
                  {preview?.employee_count ?? 0} employee{(preview?.employee_count ?? 0) === 1 ? "" : "s"} on this designation
                </span>
              )}
            </div>
            {preview?.employees?.length > 0 && (
              <div className="mt-2 text-[11px] text-muted-foreground max-h-24 overflow-auto">
                {preview.employees.slice(0, 20).map((e) => (
                  <div key={e.id}>• {e.full_name || e.emp_id || "—"}</div>
                ))}
                {preview.employees.length > 20 && (<div>…and {preview.employees.length - 20} more</div>)}
              </div>
            )}
            {/* R32 · Show WHO specifically is affected by text edits */}
            {hasEdits && editImpact?.employees?.length > 0 && (
              <div className="mt-3 pt-2 border-t border-border/60">
                <div className="text-[11px] font-semibold text-sky-800 dark:text-sky-300">
                  {editImpact.count} employee{editImpact.count === 1 ? "" : "s"} will see updated duty text:
                </div>
                <div className="mt-1 text-[11px] text-muted-foreground max-h-24 overflow-auto"
                      data-testid="tpl-prop-edit-impact-list">
                  {editImpact.employees.slice(0, 20).map((e) => (
                    <div key={e.user_id}>
                      • <b>{e.full_name || e.emp_id || "—"}</b>
                      {e.affected_duty_titles?.length ? (
                        <span className="text-muted-foreground/80"> ({e.affected_duty_titles.length} duty{e.affected_duty_titles.length === 1 ? "" : "ies"})</span>
                      ) : null}
                    </div>
                  ))}
                  {editImpact.employees.length > 20 && (<div>…and {editImpact.employees.length - 20} more</div>)}
                </div>
              </div>
            )}
          </div>

          {noOp && (
            <div className="text-[11px] text-muted-foreground flex items-start gap-2 border border-amber-300/50 bg-amber-50 dark:bg-amber-950/20 p-2 rounded-none">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              No template changes to propagate. Edit some duties on the designation first.
            </div>
          )}

          <div>
            <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Effective date</Label>
            <Input type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)}
                   data-testid="tpl-prop-valid-from" className="mt-1 rounded-none" />
          </div>

          <RadioGroup value={mode} onValueChange={setMode} className="space-y-2">
            {TPL_MODES.map((m) => (
              <div key={m.value}
                   className="flex items-start gap-2 border border-border rounded-none p-2 hover:bg-muted/40 cursor-pointer"
                   onClick={() => setMode(m.value)}>
                <RadioGroupItem value={m.value} id={`tpl-mode-${m.value}`} className="mt-1"
                                data-testid={`tpl-prop-mode-${m.value}`} />
                <label htmlFor={`tpl-mode-${m.value}`} className="flex-1 cursor-pointer">
                  <div className="text-xs font-medium">{m.label}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">{m.hint}</div>
                </label>
              </div>
            ))}
          </RadioGroup>

          {/* R32 · Edit-mode selector, only visible when the save
                 actually contains text edits. Answers Support's ask:
                 "distinguish minor text edits (update in place, keep
                 review history) from major edits (end-and-replace,
                 old reviews stay against the old text)." */}
          {hasEdits && (
            <div className="border border-sky-200 dark:border-sky-800 bg-sky-50 dark:bg-sky-950/20 rounded-none p-2 space-y-2"
                  data-testid="tpl-prop-edit-mode-section">
              <div className="text-[11px] font-semibold text-sky-800 dark:text-sky-300">
                How should the {editedTemplateIds.length} text edit{editedTemplateIds.length === 1 ? "" : "s"} be applied?
              </div>
              <RadioGroup value={editMode} onValueChange={setEditMode} className="space-y-1">
                <div className="flex items-start gap-2 p-1.5 hover:bg-white/60 dark:hover:bg-black/20 cursor-pointer"
                      onClick={() => setEditMode("update_in_place")}>
                  <RadioGroupItem value="update_in_place" id="tpl-edit-inplace" className="mt-0.5"
                                       data-testid="tpl-prop-edit-mode-inplace" />
                  <label htmlFor="tpl-edit-inplace" className="flex-1 cursor-pointer">
                    <div className="text-xs font-medium">Update in place <span className="text-muted-foreground font-normal">(minor edit)</span></div>
                    <div className="text-[11px] text-muted-foreground">Same duty row keeps its id — all past reviews continue counting against it. Use this for typo fixes / wording tweaks.</div>
                  </label>
                </div>
                <div className="flex items-start gap-2 p-1.5 hover:bg-white/60 dark:hover:bg-black/20 cursor-pointer"
                      onClick={() => setEditMode("end_and_replace")}>
                  <RadioGroupItem value="end_and_replace" id="tpl-edit-replace" className="mt-0.5"
                                       data-testid="tpl-prop-edit-mode-replace" />
                  <label htmlFor="tpl-edit-replace" className="flex-1 cursor-pointer">
                    <div className="text-xs font-medium">End-date old row + insert fresh <span className="text-muted-foreground font-normal">(major edit)</span></div>
                    <div className="text-[11px] text-muted-foreground">Old duty row is closed (valid_to = effective date); a new row starts with the updated text. Past reviews stay against the old row. Use this for material rewrites.</div>
                  </label>
                </div>
              </RadioGroup>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="tpl-prop-cancel" disabled={busy}>
            Skip
          </Button>
          <Button onClick={apply} disabled={busy || noOp || (preview?.employee_count ?? 0) === 0}
                  data-testid="tpl-prop-apply">
            {busy ? "Applying…" : `Apply to ${preview?.employee_count ?? 0} employees`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
